/**
 * Search utilities for v0.7.0
 */

import type { AdvancedSearchParams } from '../types';

/**
 * Build SQL WHERE clause from search parameters
 */
export function buildSearchWhereClause(params: AdvancedSearchParams): {
  clause: string;
  params: (string | number | null)[];
} {
  const conditions: string[] = [];
  const sqlParams: (string | number | null)[] = [];

  if (params.category) {
    conditions.push('category = ?');
    sqlParams.push(params.category);
  }

  if (params.region) {
    conditions.push('region = ?');
    sqlParams.push(params.region);
  }

  if (params.district) {
    conditions.push('district = ?');
    sqlParams.push(params.district);
  }

  if (params.priceRange) {
    if (params.priceRange.min !== null) {
      conditions.push('price_range >= ?');
      sqlParams.push(params.priceRange.min);
    }
    if (params.priceRange.max !== null) {
      conditions.push('price_range <= ?');
      sqlParams.push(params.priceRange.max);
    }
  }

  if (params.keyword) {
    conditions.push('(title LIKE ? OR description LIKE ? OR store_name LIKE ?)');
    const keywordPattern = `%${params.keyword}%`;
    sqlParams.push(keywordPattern, keywordPattern, keywordPattern);
  }

  return {
    clause: conditions.length > 0 ? `WHERE ${conditions.join(' AND ')}` : '',
    params: sqlParams,
  };
}

/**
 * Build ORDER BY clause from sort parameters
 */
export function buildSortClause(sortBy?: string, sortOrder?: string): string {
  const direction = sortOrder === 'asc' ? 'ASC' : 'DESC';

  const sortMap: Record<string, string> = {
    price: 'CAST(REPLACE(price_range, "$", "") AS INTEGER)',
    date: 'created_at',
    popularity: 'click_count',  // Use click_count for sales/popularity sorting
    featured: 'featured DESC, created_at DESC',  // Featured ads first, then by date
  };

  const column = sortMap[sortBy || 'date'] || 'created_at';
  return `ORDER BY ${column} ${direction}`;
}

/**
 * Price range presets
 */
export const PRICE_PRESETS = [
  { label: '不限', min: null, max: null },
  { label: '$ 以下', min: null, max: 500 },
  { label: '$ - $$', min: 500, max: 2000 },
  { label: '$ - $$$', min: 1000, max: 5000 },
  { label: '$$$ 以上', min: 5000, max: null },
];

/**
 * Default search params
 */
export const DEFAULT_SEARCH_PARAMS: AdvancedSearchParams = {
  sortBy: 'date',
  sortOrder: 'desc',
};

/**
 * Save search to history
 */
export function saveSearchHistory(name: string, params: AdvancedSearchParams): void {
  const history = getSearchHistory();
  const newItem = {
    id: Date.now().toString(),
    name,
    params,
    createdAt: new Date().toISOString(),
  };

  // Limit to 10 items
  const updatedHistory = [newItem, ...history].slice(0, 10);
  localStorage.setItem('search_history', JSON.stringify(updatedHistory));
}

/**
 * Get search history
 */
export function getSearchHistory(): Array<{
  id: string;
  name: string;
  params: AdvancedSearchParams;
  createdAt: string;
}> {
  const data = localStorage.getItem('search_history');
  return data ? JSON.parse(data) : [];
}

/**
 * Remove search from history
 */
export function removeSearchHistory(id: string): void {
  const history = getSearchHistory();
  const updated = history.filter((item) => item.id !== id);
  localStorage.setItem('search_history', JSON.stringify(updated));
}

/**
 * Clear search history
 */
export function clearSearchHistory(): void {
  localStorage.removeItem('search_history');
}
